"use client";
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config/apiconfig";

const AuthContext = createContext();

// Non-sensitive placeholder stored under "token". The real JWT lives ONLY in an
// httpOnly cookie; this marker just signals "logged in" to existing auth-gated
// code. The backend's TokenFromCookie middleware treats "Bearer cookie" as
// no-token and uses the real cookie instead.
const AUTH_MARKER = "cookie";

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  // The real JWT is an httpOnly cookie; this only tracks the login marker.
  const [token, setToken] = useState(localStorage.getItem("token"));

  // 🔹 Base API URL
  const API_URL = BASE_URL;

  /* ============================
        REGISTER USER
    ============================ */
  const registerUser = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/users`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {

      setError(err.response?.data?.message || "Registration failed");

      // Handle validation errors (422 status)
      if (err.response?.status === 422) {
        return {
          success: false,
          error: err.response?.data,
          validationErrors: err.response?.data?.errors,
          status: err.response?.status,
        };
      }

      return {
        success: false,
        error: err.response?.data,
        status: err.response?.status,
      };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        LOGIN
    ============================ */
  const loginUser = async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/login`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      // ✅ The real JWT is delivered as an httpOnly cookie (unreadable by JS).
      // We persist only a NON-SENSITIVE marker under "token" so existing
      // auth-gated code keeps working; the marker is never a usable credential.
      // localStorage.setItem("token", AUTH_MARKER);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.data));
      // setToken(AUTH_MARKER);
      setToken(res.data.token);
      setUser(res.data.data);
      window.dispatchEvent(new Event("auth-change"));

      return { success: true, data: res.data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data,
      };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        GOOGLE LOGIN
    ============================ */
  const googleLogin = async (idToken) => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        `${API_URL}/google-login`,
        { id_token: idToken },
        { headers: { "Content-Type": "application/json" } }
      );

      // ✅ Token delivered as httpOnly cookie; persist only the marker + user.
      // localStorage.setItem("token", AUTH_MARKER);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.data));
      // setToken(AUTH_MARKER);
      setToken(res.data.token);
      setUser(res.data.data);
      window.dispatchEvent(new Event("auth-change"));

      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        LOGOUT
    ============================ */
  const logout = async () => {
    // Ask the backend to clear the httpOnly cookie (JS cannot delete it itself).
    try {
      await axios.post(`${API_URL}/logout`);
    } catch {
      /* ignore network/expired-session errors — clear client state regardless */
    }
    localStorage.removeItem("token"); // legacy cleanup
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    window.dispatchEvent(new Event("auth-change"));
  };

  /* ============================
        FORGOT PASSWORD (Send OTP)
    ============================ */
  const forgotPassword = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/forgot-password`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      // Return OTP token from response for step 2
      return {
        success: true,
        data: { token: res.data.token, expires_at: res.data.expires_at },
      };
    } catch (err) {
      return { success: false, error: err.response?.data };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        VALIDATE OTP
    ============================ */
  const validateOtpApi = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/validate-otp`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        RESET PASSWORD
    ============================ */
  const resetPasswordApi = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/reset-password`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data };
    } finally {
      setLoading(false);
    }
  };

  /* ============================
        INITIALIZE USER FROM LOCALSTORAGE
    ============================ */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        registerUser,
        loginUser,
        googleLogin,
        logout,
        forgotPassword,
        validateOtpApi,
        resetPasswordApi,
        user,
        token,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook
export const useAuth = () => useContext(AuthContext);
